/**
 * Item labels — the sticker that goes on a cup or a box. Port of web `printHelper.js`
 * `printItemLabelTemplate` (:852) and `polling.js` `printItemLabelConfig` (:333) / `getLabels`
 * (:377) / `printLabels` (:572), which are the same document driven two ways.
 *
 * This is the ONE document whose routing does not come from `routing.ts`: the library reads the
 * item-label GROUPS itself and answers a map of `printerId → content`, so the planner asks which
 * printers this device has and matches them against that map.
 *
 * Labels are ZPL, not ESC/POS — `^XA … ^FD … ^XZ`. That needs nothing special here: a label
 * printer is saved as PLAIN, and `encode/escpos` passes a PLAIN line through as its own bytes.
 */
import { renderItemLabelContent } from "../content";
import { labelPrinters } from "../routing";
import type { Claim, LabelInput, LabelStatus, Piece, Plan, PrintContext, PrinterRecord, Skip } from "../types";
import { clone, isEmpty } from "../util";

/**
 * v1 `getLabels`: one label per unit of every sale item, numbered from 1. It is built from the
 * SALE, not from what the library routed — an excluded item still counts a label here, exactly as
 * v1 counts it.
 */
export function labelUnits(sale: any): LabelStatus[] {
  const units: LabelStatus[] = [];
  for (const item of sale?.saleItems || []) {
    const quantity = item?.quantity || 1;
    for (let index = 0; index < quantity; index++) {
      units.push({ itemId: item?.itemId || item?.mid, labelIndex: index + 1, printStatus: "Completed", printSource: "Poller" });
    }
  }
  return units;
}

export function planLabel(input: LabelInput, ctx: PrintContext): Plan {
  const branchSettings = ctx.posSettings?.itemLabelSettings;
  const settings = input.itemLabelSettings ?? branchSettings;
  if (!settings || isEmpty(settings)) return { pieces: [], skipped: [{ reason: "noLabelSettings" }] };

  // v1 checks the printer named in the BRANCH settings even when the caller passed its own copy
  const printerId = branchSettings?.printerId?.[0];
  const byMid = new Map<unknown, PrinterRecord>(labelPrinters(ctx).map((printer) => [printer.mid, printer]));
  if (!printerId || !byMid.has(printerId)) {
    // v1: ToastNotifyError("UI_ERR_SELECT_PRINTER_FROM_POS") and nothing prints
    return { pieces: [], skipped: [{ reason: "noLabelPrinter", printerId }] };
  }

  // the library mutates its input (it moves `barcode` last in the template)
  const content = renderItemLabelContent(clone(input.sale), clone(settings), input.printInfo);

  const pieces: Piece[] = [];
  const skipped: Skip[] = [];
  for (const [routedTo, text] of Object.entries(content)) {
    const printer = byMid.get(routedTo);
    if (!printer) {
      // v1 printHelper sends to `undefined` here and the shell throws; v1 polling skips it, as here
      skipped.push({ reason: "labelPrinterMissing", printerId: routedTo });
      continue;
    }
    if (!text) continue;
    pieces.push({ document: "label", destination: { to: "printer", printer }, copies: 1, source: { kind: "text", text } });
  }

  let claim: Claim | undefined;
  if (input.poller) {
    const infos = labelUnits(input.sale);
    if (infos.length) claim = { of: "labels", saleId: input.sale?.mid, infos };
  }
  return { pieces, skipped, claim };
}
